import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import NewsCard from "@/components/NewsCard";
import { api } from "@/lib/api";
import { Twitter, Instagram, AtSign } from "lucide-react";

function AuthorAvatar({ author }) {
  const [failed, setFailed] = useState(false);
  const initials = author.name?.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();
  const showImg = author.avatar && !failed;
  return (
    <div className="w-24 h-24 md:w-32 md:h-32 rounded-full overflow-hidden shrink-0 accent-bg flex items-center justify-center" data-testid="author-avatar">
      {showImg ? (
        <img
          src={author.avatar}
          alt={author.name}
          className="w-full h-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        <span className="font-headline text-white text-3xl md:text-4xl uppercase">{initials}</span>
      )}
    </div>
  );
}

export default function AuthorPage() {
  const { slug } = useParams();
  const [author, setAuthor] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/authors/${slug}`).then((r) => {
      setAuthor(r.data.author);
      setArticles(r.data.articles || []);
    }).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <><Header /><main className="py-20 text-center" style={{ color: "var(--text-muted)" }}>Cargando...</main></>;
  if (!author) return <><Header /><main className="py-20 text-center"><h1 className="font-headline text-3xl">Autor no encontrado</h1><Link to="/" className="accent-text mt-3 inline-block">← Volver</Link></main></>;

  return (
    <>
      <Header />
      <main className="max-w-[1100px] mx-auto px-4 md:px-8 py-10" data-testid="author-page">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-6 pb-8 border-b" style={{ borderColor: "var(--border)" }}>
          <AuthorAvatar author={author} />
          <div className="flex-1">
            <p className="overline accent-text mb-2">AUTOR</p>
            <h1 className="font-headline uppercase text-3xl md:text-5xl tracking-tight" data-testid="author-name">
              {author.name}
            </h1>
            {author.bio && (
              <p className="mt-3 text-base md:text-lg leading-relaxed" style={{ color: "var(--text-muted)" }}>
                {author.bio}
              </p>
            )}
            <div className="flex flex-wrap gap-3 mt-4 text-sm">
              {author.twitter && (
                <a href={`https://twitter.com/${author.twitter.replace("@","")}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 accent-text hover:underline" data-testid="author-twitter">
                  <Twitter size={14} /> {author.twitter}
                </a>
              )}
              {author.instagram && (
                <a href={`https://instagram.com/${author.instagram.replace("@","")}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 accent-text hover:underline">
                  <Instagram size={14} /> {author.instagram}
                </a>
              )}
            </div>
          </div>
          <div className="text-right">
            <p className="font-headline text-4xl md:text-5xl">{articles.length}</p>
            <p className="overline" style={{ color: "var(--text-muted)" }}>NOTAS</p>
          </div>
        </div>

        <div className="mt-10">
          {articles.length === 0 ? (
            <p className="text-center py-12" style={{ color: "var(--text-muted)" }}>Aún no hay notas publicadas por este autor.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10">
              {articles.map((a) => <NewsCard key={a.id} article={a} />)}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
